/* eslint-disable react-hooks/exhaustive-deps */
import { EuiBadge, EuiButton, EuiPageHeader, EuiSpacer } from "@elastic/eui";
import i18n from "i18n-js";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Access,
  CustomizeFilter,
  CustomizeTablePagination,
  DeleteAction,
  EditAction,
  FilterInput,
} from "../../../components";
import { Paths, PERMISSIONS } from "../../../constants";
import { getEmployeesApi, getSubjectsApi } from "../../../network";
import {
  archiveManyTeacherRole,
  deleteManyTeacherRole,
  deleteOneTeacherRole,
  getListTeacherRole,
  unarchiveManyTeacherRole,
} from "../../../redux/actions/teacher-role";
import { formatToLocalDate, getInitParams } from "../../../utils";

interface IEmployee {
  _id: string;
  name: string;
}

interface ISubject {
  _id: string;
  name: string;
}

const List: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useState(getInitParams());

  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [subjects, setSubjects] = useState<ISubject[]>([]);

  const getListEmployee = async (searchValue?: string) => {
    const responseEmployee = await getEmployeesApi({ name: searchValue });
    if (responseEmployee && responseEmployee.data) {
      setEmployees(responseEmployee.data);
    }
  };

  const getListSubject = async (searchValue?: string) => {
    const response = await getSubjectsApi({ name: searchValue });
    if (response && response.data) {
      setSubjects(response.data);
    }
  };

  useEffect(() => {
    getListEmployee();
    getListSubject();
  }, []);

  useEffect(() => {
    dispatch(getListTeacherRole(params));
  }, [params]);

  const { teacherRoles, total } = useSelector(
    (state: RootStateOrAny) => state?.teacherRole?.list
  );

  const { isLoadingRequest } = useSelector(
    (state: RootStateOrAny) => state.teacherRole
  );

  const onChangePage = (value: any) => {
    setParams({ ...params, offset: value });
  };

  const onDeleteMany = (ids: string[]) => {
    dispatch(deleteManyTeacherRole({ ids }));
  };

  const onArchiveMany = (ids: string[]) => {
    dispatch(archiveManyTeacherRole({ ids, isArchived: true }));
  };

  const onUnarchiveMany = (ids: string[]) => {
    dispatch(unarchiveManyTeacherRole({ ids, isArchived: false }));
  };

  const columns = [
    {
      field: "name",
      name: "Tên",
      sortable: false,
    },
    {
      field: "employeeId",
      name: "Nhân viên",
      sortable: false,
    },
    {
      field: "provinceId",
      name: "Địa điểm làm việc",
      sortable: false,
    },
    {
      field: "startWorkingDate",
      name: "Ngày bắt đầu",
      sortable: true,
      render: (date: any) => (
        <p>{moment(date).format("DD-MM-YYY HH:MM:SS A")}</p>
      ),
    },
    {
      field: "isArchived",
      name: "Lưu trữ",
      sortable: false,
      render: (isArchived: boolean) => (
        <EuiBadge color={isArchived ? "warning" : "ghost"}>
          {isArchived ? "Đã lưu trữ" : "Không"}
        </EuiBadge>
      ),
    },
    {
      field: "createdAt",
      name: "Khởi tạo",
      render: (time: string) => <p>{formatToLocalDate(time)}</p>,
    },
    {
      name: "Hành động",
      actions: [
        {
          render: (item: any) => {
            return (
              <Access permissionKey={PERMISSIONS.TEACHER_ROLE_UPDATE}>
                <EditAction
                  link={Paths.TEACHER_ROLE.EDIT.replace(":id", item._id)}
                />
              </Access>
            );
          },
        },
        {
          render: (item: any) => {
            return (
              <Access permissionKey={PERMISSIONS.TEACHER_ROLE_DELETE}>
                <DeleteAction
                  onOk={() => {
                    dispatch(deleteOneTeacherRole({ id: item._id }));
                  }}
                />
              </Access>
            );
          },
        },
      ],
    },
  ];

  const onChangeItemsPerPage = (per: any) => {
    setParams({ ...params, limit: per });
  };

  const filterInputs: FilterInput[] = [
    { name: "name", label: "Tên", type: "input", visible: true },
    { name: "name", label: "Tên", type: "input" },
    {
      name: "employeeId",
      label: "Nhân viên quản lý",
      type: "select",
      options: [{ value: "", text: "" }, ...employees].map((item: any) => ({
        value: item._id,
        text: item.name,
      })),
    },
    {
      name: "subjectId",
      label: "Môn học",
      type: "select",
      options: [{ value: "", text: "" }, ...subjects].map((item: any) => ({
        value: item._id,
        text: item.name,
      })),
    },
    {
      name: "isArchived",
      label: "Lưu trữ",
      type: "select",
      options: [
        { value: "", text: "Tất cả" },
        { value: "true", text: "Đã lưu trữ" },
        { value: "false", text: "Không" },
      ],
    },
  ];

  const onFilter = (data: any) => {
    const newFilter: any = { ...params, ...data };
    for (let key in newFilter) {
      if (newFilter[key] === "" || newFilter[key] === undefined) {
        delete newFilter[key];
      }
    }
    setParams({ ...newFilter, offset: 0 });
  };

  return (
    <>
      <EuiPageHeader
        bottomBorder
        className="mtb-page-header"
        pageTitle="Phân môn giảng dạy"
        rightSideItems={[
          <Access permissionKey={PERMISSIONS.TEACHER_ROLE_CREATE}>
            <EuiButton
              iconSide="left"
              iconType="plusInCircle"
              fill
              onClick={() => navigate({ pathname: Paths.TEACHER_ROLE.CREATE })}
            >
              {i18n.t("common.createButton")}
            </EuiButton>
          </Access>,
        ]}
      />
      <EuiSpacer size="s" />

      <CustomizeFilter
        list={filterInputs}
        onFilter={onFilter}
        page={Paths.TEACHER_ROLE.LIST}
      />

      <EuiSpacer size="s" />

      <CustomizeTablePagination
        columns={columns}
        body={teacherRoles}
        isLoading={isLoadingRequest}
        pagination={params}
        total={total}
        isSelectable={true}
        onChangePage={onChangePage}
        onChangeItemsPerPage={onChangeItemsPerPage}
        onDeleteMany={onDeleteMany}
        onArchiveMany={onArchiveMany}
        onUnarchiveMany={onUnarchiveMany}
      />
    </>
  );
};

export default List;
